import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { CircularProgress } from "@mui/material";

import {
  deleteSiteInstructionMedia,
  getSiteInstructions,
  saveSiteInstructionsText,
  uploadSiteInstructionMedia,
} from "../../../_redux/franchises/franchisesCrud";
import "../../../../../../_metronic/_assets/sass/components/_site-instructions-section.scss";

const IMAGE_SLOTS = ["image_1", "image_2", "image_3", "image_4", "image_5"];
const VIDEO_SLOTS = ["video_1", "video_2"];
const PDF_SLOT = "pdf";
const FLOOR_OPTIONS = [
  { value: "G", label: "Ground Floor" },
  { value: "1F", label: "First Floor" },
];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_VIDEO_BYTES = 150 * 1024 * 1024;
const MAX_PDF_BYTES = 10 * 1024 * 1024;

/** @param {File} file @returns {string} Lowercase extension without dot */
function fileExtension(file) {
  const name = file?.name || "";
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
}

const IMAGE_EXT = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "heic",
  "heif",
]);
const VIDEO_EXT = new Set([
  "mp4",
  "webm",
  "mov",
  "mpeg",
  "mpg",
  "m4v",
  "avi",
]);

/**
 * Derives a display filename from a media URL.
 *
 * @param {string} url - Absolute or relative URL.
 * @returns {string} Last path segment or fallback label.
 */
function fileLabelFromUrl(url) {
  if (!url || typeof url !== "string") return "File";
  try {
    const base =
      url.startsWith("http") || url.startsWith("//")
        ? undefined
        : import.meta.env.REACT_APP_SITE_URL || window.location.origin;
    const u = new URL(url, base);
    const parts = u.pathname.split("/").filter(Boolean);
    const seg = parts.pop();
    return seg ? decodeURIComponent(seg) : "File";
  } catch {
    return "File";
  }
}

/**
 * Returns the floor-specific instruction payload when the API returns a `floors` map.
 *
 * @param {object} raw - API response data.
 * @param {"G"|"1F"} floor - Floor to read.
 * @returns {object} Floor instruction payload or original payload for legacy responses.
 */
function floorPayloadFromResponse(raw, floor) {
  if (!raw || typeof raw !== "object") return raw;
  const floors = raw.floors;
  if (floors && typeof floors === "object" && !Array.isArray(floors)) {
    return floors[floor] && typeof floors[floor] === "object"
      ? floors[floor]
      : {};
  }
  return raw;
}

/**
 * Normalizes instruction API `data` (GET, POST save, or upload) into text, what3words, and slot URLs.
 * Supports nested `images` / `videos` / `pdf_url` plus legacy flat keys and `urls` / `media`.
 *
 * @param {object} raw - Response `data` object.
 * @param {"G"|"1F"} [floor="G"] - Floor to read when `raw.floors` is present.
 * @returns {{ text: string, what3words: string, slots: Record<string, string> }} Parsed instructions.
 */
function normalizeInstructionsData(raw, floor = "G") {
  const floorRaw = floorPayloadFromResponse(raw, floor);
  if (!floorRaw || typeof floorRaw !== "object") {
    return { text: "", what3words: "", slots: {} };
  }
  const text =
    typeof floorRaw.instructions === "string"
      ? floorRaw.instructions
      : typeof floorRaw.instruction === "string"
      ? floorRaw.instruction
      : "";
  let what3words = "";
  const what3wordsValue = Object.prototype.hasOwnProperty.call(
    floorRaw,
    "what3words"
  )
    ? floorRaw.what3words
    : floorRaw.passphrase;
  if (what3wordsValue === null || what3wordsValue === undefined) {
    what3words = "";
  } else if (typeof what3wordsValue === "string") {
    what3words = what3wordsValue;
  } else {
    what3words = String(what3wordsValue);
  }

  const slots = {};

  if (Object.prototype.hasOwnProperty.call(floorRaw, "pdf_url")) {
    if (typeof floorRaw.pdf_url === "string" && floorRaw.pdf_url) {
      slots[PDF_SLOT] = floorRaw.pdf_url;
    }
  }

  if (
    floorRaw.images &&
    typeof floorRaw.images === "object" &&
    !Array.isArray(floorRaw.images)
  ) {
    IMAGE_SLOTS.forEach((k) => {
      const u = floorRaw.images[k];
      if (typeof u === "string" && u) slots[k] = u;
    });
  }
  if (
    floorRaw.videos &&
    typeof floorRaw.videos === "object" &&
    !Array.isArray(floorRaw.videos)
  ) {
    VIDEO_SLOTS.forEach((k) => {
      const u = floorRaw.videos[k];
      if (typeof u === "string" && u) slots[k] = u;
    });
  }

  const slotKeys = [PDF_SLOT, ...IMAGE_SLOTS, ...VIDEO_SLOTS];
  slotKeys.forEach((k) => {
    if (slots[k]) return;
    const v = floorRaw[k];
    if (v && typeof v === "string") slots[k] = v;
  });
  if (floorRaw.urls && typeof floorRaw.urls === "object") {
    slotKeys.forEach((k) => {
      if (slots[k]) return;
      if (floorRaw.urls[k] && typeof floorRaw.urls[k] === "string") {
        slots[k] = floorRaw.urls[k];
      }
    });
  }
  if (floorRaw.media && typeof floorRaw.media === "object") {
    slotKeys.forEach((k) => {
      if (slots[k]) return;
      if (floorRaw.media[k] && typeof floorRaw.media[k] === "string") {
        slots[k] = floorRaw.media[k];
      }
    });
  }

  if (!Object.prototype.hasOwnProperty.call(floorRaw, "pdf_url")) {
    const v = floorRaw[PDF_SLOT];
    if (v && typeof v === "string" && !slots[PDF_SLOT]) slots[PDF_SLOT] = v;
  }

  return { text, what3words, slots };
}

/**
 * Whether normalized slot map contains at least one URL to apply to UI state.
 *
 * @param {Record<string, string>|undefined|null} slots - Normalized slot URLs.
 * @returns {boolean} True when `setSlotUrls` should run from this payload.
 */
function hasNormalizedMediaSlots(slots) {
  return Boolean(
    slots && typeof slots === "object" && Object.keys(slots).length > 0
  );
}

/**
 * Builds a stable key for floor-specific media mutations.
 *
 * @param {"G"|"1F"} floor - Instruction floor.
 * @param {string} slot - Media slot.
 * @returns {string} Mutation key.
 */
function mediaMutationKey(floor, slot) {
  return `${floor}:${slot}`;
}

/**
 * Parses axios GET /instructions response.
 *
 * @param {object} res - Axios response object.
 * @param {"G"|"1F"} [floor="G"] - Floor to read when response includes floors.
 * @returns {{ text: string, what3words: string, slots: Record<string, string> }} Parsed instructions.
 */
function parseInstructionsResponse(res, floor = "G") {
  const raw = res?.data?.data ?? res?.data ?? {};
  return normalizeInstructionsData(raw, floor);
}

/**
 * Applies normalized instruction `data` to React state (instant preview after POST/upload/delete).
 * Updates text and what3words always; updates slot URLs only when normalized slots are non-empty
 * (avoids wiping UI on partial/empty media fragments).
 *
 * @param {object} raw - Response `data` object.
 * @param {object} setters - State setters `{ setInstructionsDraft, setWhat3wordsDraft, setSlotUrls }`.
 * @param {"G"|"1F"} [floor="G"] - Floor to read when response includes floors.
 * @returns {boolean} True when `setSlotUrls` was applied; false if callers should refetch (e.g. `loadInstructions({ quiet: true })`).
 */
function applyInstructionsDataToState(raw, setters, floor = "G") {
  const { setInstructionsDraft, setWhat3wordsDraft, setSlotUrls } = setters;
  const normalized = normalizeInstructionsData(raw, floor);
  setInstructionsDraft(normalized.text);
  setWhat3wordsDraft(normalized.what3words);
  if (hasNormalizedMediaSlots(normalized.slots)) {
    setSlotUrls(normalized.slots);
    return true;
  }
  return false;
}

/**
 * Validates file size and MIME type before upload.
 *
 * @param {string} slot - Instruction slot key.
 * @param {File} file - Selected file.
 * @returns {string|null} Error message or null if valid.
 */
function validateInstructionFile(slot, file) {
  if (!file) return "No file selected.";
  const ext = fileExtension(file);
  if (slot === PDF_SLOT) {
    if (file.type && file.type !== "application/pdf") {
      return "Please choose a PDF file.";
    }
    if (!file.type && ext && ext !== "pdf") {
      return "Please choose a PDF file.";
    }
    if (file.size > MAX_PDF_BYTES) {
      return "PDF must be 10 MB or smaller.";
    }
    return null;
  }
  if (slot.startsWith("image_")) {
    const typeOk = file.type?.startsWith("image/");
    const extOk = ext && IMAGE_EXT.has(ext);
    if (file.type && !typeOk) {
      return "Please choose an image file.";
    }
    if (!file.type && ext && !extOk) {
      return "Please choose an image file.";
    }
    if (!file.type && !ext) {
      return "Please choose an image file.";
    }
    if (file.size > MAX_IMAGE_BYTES) {
      return "Image must be 5 MB or smaller.";
    }
    return null;
  }
  if (slot.startsWith("video_")) {
    const typeOk = file.type?.startsWith("video/");
    const extOk = ext && VIDEO_EXT.has(ext);
    if (file.type && !typeOk) {
      return "Please choose a video file.";
    }
    if (!file.type && ext && !extOk) {
      return "Please choose a video file.";
    }
    if (!file.type && !ext) {
      return "Please choose a video file.";
    }
    if (file.size > MAX_VIDEO_BYTES) {
      return "Video must be 150 MB or smaller.";
    }
    return null;
  }
  return "Unknown slot.";
}

/**
 * Site instructions: floor-specific written guidance plus fixed PDF / image / video slots.
 * Loads from GET, saves text and what3words via POST, uploads and deletes media per floor slot.
 *
 * @param {object} props - Component props.
 * @param {string|number} props.entityId - Site id (required when section is shown).
 * @param {function} props.setSnack - Snackbar setter `{ call, type, message }`.
 * @returns {JSX.Element} Floor-aware instructions subsection for the site side panel.
 */
export function SiteInstructionsSection({
  entityId,
  setSnack,
}) {
  const [loading, setLoading] = useState(true);
  const [activeFloor, setActiveFloor] = useState("G");
  const [instructionsDraft, setInstructionsDraft] = useState("");
  const [what3wordsDraft, setWhat3wordsDraft] = useState("");
  const [slotUrls, setSlotUrls] = useState({});
  const [saveTextLoading, setSaveTextLoading] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [deletingSlot, setDeletingSlot] = useState(null);
  const [preview, setPreview] = useState({ open: false, kind: null, src: null });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const idSafe = useMemo(
    () => String(entityId ?? "").replace(/[^\w.-]/g, "_"),
    [entityId]
  );
  const fieldIdPrefix = `site-si-${idSafe}`;
  const textareaId = `${fieldIdPrefix}-textarea`;
  const what3wordsId = `${fieldIdPrefix}-what3words`;
  const floorSafe = activeFloor.replace(/[^\w.-]/g, "_");
  const fileInputId = (slot) => `${fieldIdPrefix}-${floorSafe}-file-${slot}`;
  const pdfReplaceId = `${fieldIdPrefix}-${floorSafe}-pdf-replace`;
  const pdfEmptyId = `${fieldIdPrefix}-${floorSafe}-pdf-empty`;

  const loadSeqRef = useRef(0);
  const aliveRef = useRef(true);
  const entityRef = useRef(entityId);
  const floorRef = useRef(activeFloor);

  useEffect(() => {
    entityRef.current = entityId;
    floorRef.current = activeFloor;
  }, [entityId, activeFloor]);

  useEffect(() => {
    setDeleteConfirm(null);
    setSaveTextLoading(false);
    setUploadingSlot(null);
    setDeletingSlot(null);
    setPreview({ open: false, kind: null, src: null });
  }, [entityId]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      loadSeqRef.current += 1;
    };
  }, []);

  /**
   * True while this instance is mounted and `entityId` / floor have not changed
   * since the caller captured `forEntity` / `forFloor`.
   *
   * @param {string|number} forEntity - `entityId` at the time the async work started.
   * @param {"G"|"1F"} forFloor - Active floor when the work started.
   * @returns {boolean} Whether it is safe to apply results from that work.
   */
  const scopeMatches = useCallback((forEntity, forFloor) => {
    return (
      aliveRef.current &&
      entityRef.current === forEntity &&
      floorRef.current === forFloor
    );
  }, []);

  const notify = useCallback(
    (type, message) => {
      setSnack({ call: true, type, message });
    },
    [setSnack]
  );

  /**
   * Whether the current floor/slot has an upload or delete mutation in progress.
   *
   * @param {string} slot - Media slot.
   * @returns {boolean} True when controls for this slot should be disabled.
   */
  const isSlotBusy = useCallback(
    (slot) => {
      const key = mediaMutationKey(activeFloor, slot);
      return uploadingSlot === key || deletingSlot === key;
    },
    [activeFloor, deletingSlot, uploadingSlot]
  );

  const loadInstructions = useCallback(
    async (options = {}) => {
      const { quiet } = options;
      if (!entityId) return;
      const forEntity = entityId;
      const forFloor = activeFloor;
      const runId = ++loadSeqRef.current;
      if (!quiet) {
        setLoading(true);
        setInstructionsDraft("");
        setWhat3wordsDraft("");
        setSlotUrls({});
      }
      try {
        const res = await getSiteInstructions(entityId);
        if (
          runId !== loadSeqRef.current ||
          !scopeMatches(forEntity, forFloor)
        ) {
          return;
        }
        const { text, what3words, slots } = parseInstructionsResponse(
          res,
          forFloor
        );
        setInstructionsDraft(text);
        setWhat3wordsDraft(what3words);
        setSlotUrls(slots);
      } catch (e) {
        if (
          runId !== loadSeqRef.current ||
          !scopeMatches(forEntity, forFloor)
        ) {
          return;
        }
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Could not load instructions.";
        if (!quiet) {
          notify("error", msg);
          setInstructionsDraft("");
          setWhat3wordsDraft("");
          setSlotUrls({});
        } else {
          notify("error", "Could not refresh instructions.");
        }
      } finally {
        if (
          runId === loadSeqRef.current &&
          !quiet &&
          scopeMatches(forEntity, forFloor)
        ) {
          setLoading(false);
        }
      }
    },
    [activeFloor, entityId, notify, scopeMatches]
  );

  useEffect(() => {
    loadInstructions();
  }, [loadInstructions]);

  const handleSaveText = () => {
    const forEntity = entityId;
    const forFloor = activeFloor;
    setSaveTextLoading(true);
    saveSiteInstructionsText(
      entityId,
      instructionsDraft,
      what3wordsDraft,
      forFloor
    )
      .then((res) => {
        if (!scopeMatches(forEntity, forFloor)) return;
        notify("success", res.data?.message || "Instructions saved.");
        const raw = res?.data?.data;
        if (raw && typeof raw === "object") {
          const slotsSynced = applyInstructionsDataToState(
            raw,
            {
              setInstructionsDraft,
              setWhat3wordsDraft,
              setSlotUrls,
            },
            forFloor
          );
          if (!slotsSynced) {
            return loadInstructions({ quiet: true });
          }
        } else {
          return loadInstructions({ quiet: true });
        }
      })
      .catch((e) => {
        if (!scopeMatches(forEntity, forFloor)) return;
        notify(
          "error",
          e?.response?.data?.message || "Failed to save instructions."
        );
      })
      .finally(() => {
        if (!aliveRef.current || entityRef.current !== forEntity) return;
        setSaveTextLoading(false);
      });
  };

  const handleFileChosen = (slot, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const err = validateInstructionFile(slot, file);
    if (err) {
      notify("error", err);
      return;
    }
    const forEntity = entityId;
    const forFloor = activeFloor;
    const mutationKey = mediaMutationKey(forFloor, slot);
    setUploadingSlot(mutationKey);
    uploadSiteInstructionMedia(entityId, slot, file, forFloor)
      .then((res) => {
        if (!scopeMatches(forEntity, forFloor)) return;
        notify("success", res.data?.message || "Upload complete.");
        const raw = res?.data?.data;
        if (raw && typeof raw === "object") {
          const slotsSynced = applyInstructionsDataToState(
            raw,
            {
              setInstructionsDraft,
              setWhat3wordsDraft,
              setSlotUrls,
            },
            forFloor
          );
          if (!slotsSynced) {
            return loadInstructions({ quiet: true });
          }
        } else {
          return loadInstructions({ quiet: true });
        }
      })
      .catch((e) => {
        if (!scopeMatches(forEntity, forFloor)) return;
        notify(
          "error",
          e?.response?.data?.message || "Upload failed."
        );
      })
      .finally(() => {
        setUploadingSlot((current) =>
          current === mutationKey ? null : current
        );
      });
  };

  const runDelete = () => {
    const slot = deleteConfirm?.slot;
    const floor = deleteConfirm?.floor || activeFloor;
    if (!slot) return;
    const forEntity = entityId;
    const forFloor = floor;
    const mutationKey = mediaMutationKey(forFloor, slot);
    setDeletingSlot(mutationKey);
    deleteSiteInstructionMedia(entityId, slot, forFloor)
      .then((res) => {
        if (!scopeMatches(forEntity, forFloor)) return;
        notify("success", res.data?.message || "Removed.");
        setDeleteConfirm(null);
        const raw = res?.data?.data;
        if (raw && typeof raw === "object") {
          const slotsSynced = applyInstructionsDataToState(
            raw,
            {
              setInstructionsDraft,
              setWhat3wordsDraft,
              setSlotUrls,
            },
            forFloor
          );
          if (!slotsSynced) {
            return loadInstructions({ quiet: true });
          }
        } else {
          return loadInstructions({ quiet: true });
        }
      })
      .catch((e) => {
        if (!scopeMatches(forEntity, forFloor)) return;
        notify(
          "error",
          e?.response?.data?.message || "Delete failed."
        );
      })
      .finally(() => {
        setDeletingSlot((current) =>
          current === mutationKey ? null : current
        );
      });
  };

  const imageUsed = IMAGE_SLOTS.filter((s) => slotUrls[s]).length;

  /**
   * Switches the active instruction floor and closes any pending delete confirmation
   * so destructive actions cannot be confirmed against a hidden floor.
   *
   * @param {"G"|"1F"} floor - Floor selected by the user.
   */
  const handleFloorChange = (floor) => {
    if (floor === activeFloor) return;
    setDeleteConfirm(null);
    setActiveFloor(floor);
  };

  const openPreview = (kind, src) => {
    setPreview({ open: true, kind, src });
  };

  /**
   * Activates a media card on Enter or Space (ARIA button keyboard support).
   *
   * @param {React.KeyboardEvent} e - Key event from the card.
   * @param {() => void} activate - Handler to run (e.g. open preview).
   */
  const handleMediaCardKeyDown = (e, activate) => {
    if (e.key === "Enter" || e.key === " " || e.code === "Space") {
      e.preventDefault();
      activate();
    }
  };

  if (!entityId) return null;

  const sectionTitle = "Site Instructions";

  return (
    <div
      className="site-instructions-section"
      data-instructions-target="site"
      data-instructions-entity-id={String(entityId)}
    >
      <h2 className="si-section-title">{sectionTitle}</h2>
      <p className="si-section-subtitle">
        Provide guidance for accessing the site, operating locks, and other
        on-site information.
      </p>

      {loading ? (
        <div className="text-center py-5">
          <CircularProgress size={28} />
        </div>
      ) : (
        <>
          <div className="si-floor-control mb-4">
            <span className="si-label d-block mb-2">Floor</span>
            <div
              className="si-floor-options"
              role="group"
              aria-label="Instruction floor"
            >
              {FLOOR_OPTIONS.map((floor) => (
                <button
                  key={floor.value}
                  type="button"
                  className={`si-floor-option${
                    activeFloor === floor.value ? " is-active" : ""
                  }`}
                  aria-pressed={activeFloor === floor.value}
                  onClick={() => handleFloorChange(floor.value)}
                >
                  <span className="si-floor-code">{floor.value}</span>
                  <span className="si-floor-name">{floor.label}</span>
                </button>
              ))}
            </div>
          </div>
          <label className="si-label" htmlFor={textareaId}>
            Written instructions
          </label>
          <textarea
            id={textareaId}
            className="form-control si-textarea"
            placeholder="e.g. Enter via the south gate. The keypad code is rotated weekly - call the manager 30 minutes before arrival to confirm."
            value={instructionsDraft}
            onChange={(e) => setInstructionsDraft(e.target.value)}
            rows={5}
          />
          <label className="si-label mt-3" htmlFor={what3wordsId}>
            what3words
          </label>
          <input
            id={what3wordsId}
            type="text"
            className="form-control"
            autoComplete="off"
            placeholder="Enter what3words for this floor"
            value={what3wordsDraft}
            onChange={(e) => setWhat3wordsDraft(e.target.value)}
          />
          <div className="si-text-actions">
            <button
              type="button"
              className="btn btn-dark btn-sm"
              disabled={saveTextLoading}
              onClick={handleSaveText}
            >
              {saveTextLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                "Save instructions"
              )}
            </button>
          </div>

          <div className="si-media-block">
            <div className="si-media-header">
              <span className="si-media-label">Images</span>
              <span className="si-media-count">
                {imageUsed} of {IMAGE_SLOTS.length} slots used
              </span>
            </div>
            <div className="si-slot-grid">
              {IMAGE_SLOTS.map((slot, idx) => {
                const url = slotUrls[slot];
                const busy = isSlotBusy(slot);
                return (
                  <div key={slot}>
                    {url ? (
                      <div
                        className="si-slot si-filled"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          handleMediaCardKeyDown(e, () =>
                            openPreview("image", url)
                          )
                        }
                        onClick={() => openPreview("image", url)}
                      >
                        <span className="si-slot-number">{idx + 1}</span>
                        <img src={url} alt="" />
                        <div className="si-slot-overlay">
                          <button
                            type="button"
                            className="si-slot-action"
                            aria-label="View image"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPreview("image", url);
                            }}
                          >
                            <VisibilityIcon style={{ fontSize: 16 }} />
                          </button>
                          <button
                            type="button"
                            className="si-slot-action si-delete"
                            aria-label="Delete image"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm({ slot, floor: activeFloor });
                            }}
                          >
                            <DeleteIcon style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="si-slot si-empty mb-0"
                          disabled={busy}
                          aria-label={`Upload image slot ${idx + 1}`}
                          onClick={() =>
                            document.getElementById(fileInputId(slot))?.click()
                          }
                        >
                          {busy ? (
                            <CircularProgress size={24} />
                          ) : (
                            <>
                              <span className="si-plus">+</span>
                              <span className="si-slot-label">
                                Image {idx + 1}
                              </span>
                            </>
                          )}
                        </button>
                        <input
                          id={fileInputId(slot)}
                          type="file"
                          className="si-file-input"
                          tabIndex={-1}
                          accept="image/*"
                          disabled={busy}
                          aria-hidden={true}
                          onChange={(e) => handleFileChosen(slot, e)}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="si-media-block">
            <div className="si-media-header">
              <span className="si-media-label">Videos</span>
              <span className="si-media-count">
                {VIDEO_SLOTS.filter((s) => slotUrls[s]).length} of{" "}
                {VIDEO_SLOTS.length} slots used
              </span>
            </div>
            <div className="si-slot-grid si-videos">
              {VIDEO_SLOTS.map((slot, idx) => {
                const url = slotUrls[slot];
                const busy = isSlotBusy(slot);
                return (
                  <div key={slot}>
                    {url ? (
                      <div
                        className="si-slot si-filled si-video"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          handleMediaCardKeyDown(e, () =>
                            openPreview("video", url)
                          )
                        }
                        onClick={() => openPreview("video", url)}
                      >
                        <span className="si-slot-number">{idx + 1}</span>
                        <div className="si-video-thumb">
                          <div className="si-video-thumb-icon" />
                        </div>
                        <div className="si-slot-overlay">
                          <button
                            type="button"
                            className="si-slot-action"
                            aria-label="Play video"
                            onClick={(e) => {
                              e.stopPropagation();
                              openPreview("video", url);
                            }}
                          >
                            <VisibilityIcon style={{ fontSize: 16 }} />
                          </button>
                          <button
                            type="button"
                            className="si-slot-action si-delete"
                            aria-label="Delete video"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm({ slot, floor: activeFloor });
                            }}
                          >
                            <DeleteIcon style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="si-slot si-empty si-video mb-0"
                          disabled={busy}
                          aria-label={`Upload video slot ${idx + 1}`}
                          onClick={() =>
                            document.getElementById(fileInputId(slot))?.click()
                          }
                        >
                          {busy ? (
                            <CircularProgress size={24} />
                          ) : (
                            <>
                              <span className="si-plus">+</span>
                              <span className="si-slot-label">
                                Video {idx + 1}
                              </span>
                            </>
                          )}
                        </button>
                        <input
                          id={fileInputId(slot)}
                          type="file"
                          className="si-file-input"
                          tabIndex={-1}
                          accept="video/*"
                          disabled={busy}
                          aria-hidden={true}
                          onChange={(e) => handleFileChosen(slot, e)}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="si-media-block">
            <div className="si-media-header">
              <span className="si-media-label">PDF Document</span>
              <span className="si-media-count">
                {slotUrls[PDF_SLOT] ? "1 of 1 slot used" : "0 of 1 slot used"}
              </span>
            </div>
            {slotUrls[PDF_SLOT] ? (
              <div className="si-pdf-slot">
                <div className="si-pdf-icon">PDF</div>
                <div className="si-pdf-info">
                  <div className="si-pdf-name">
                    {fileLabelFromUrl(slotUrls[PDF_SLOT])}
                  </div>
                  <div className="si-pdf-meta">Uploaded document</div>
                </div>
                <div className="si-pdf-actions">
                  <button
                    type="button"
                    className="si-icon-btn"
                    onClick={() => openPreview("pdf", slotUrls[PDF_SLOT])}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    className="si-icon-btn mb-0"
                    aria-label="Replace PDF document"
                    disabled={isSlotBusy(PDF_SLOT)}
                    onClick={() =>
                      document.getElementById(pdfReplaceId)?.click()
                    }
                  >
                    Replace
                  </button>
                  <input
                    id={pdfReplaceId}
                    type="file"
                    className="si-file-input"
                    tabIndex={-1}
                    accept="application/pdf"
                    disabled={isSlotBusy(PDF_SLOT)}
                    aria-hidden={true}
                    onChange={(e) => handleFileChosen(PDF_SLOT, e)}
                  />
                  <button
                    type="button"
                    className="si-icon-btn si-danger"
                    onClick={() =>
                      setDeleteConfirm({ slot: PDF_SLOT, floor: activeFloor })
                    }
                  >
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className={`si-pdf-slot si-empty mb-0${
                    isSlotBusy(PDF_SLOT) ? " align-items-center" : ""
                  }`}
                  disabled={isSlotBusy(PDF_SLOT)}
                  aria-label="Upload PDF document, max 10 megabytes"
                  onClick={() =>
                    document.getElementById(pdfEmptyId)?.click()
                  }
                >
                  <div className="si-pdf-icon">PDF</div>
                  <div className="si-pdf-info">
                    {isSlotBusy(PDF_SLOT) ? (
                      <CircularProgress size={24} />
                    ) : (
                      <>
                        <div className="si-pdf-name" style={{ color: "#6b7280" }}>
                          No PDF uploaded
                        </div>
                        <div className="si-pdf-meta">
                          Click to upload (max 10 MB)
                        </div>
                      </>
                    )}
                  </div>
                </button>
                <input
                  id={pdfEmptyId}
                  type="file"
                  className="si-file-input"
                  tabIndex={-1}
                  accept="application/pdf"
                  disabled={isSlotBusy(PDF_SLOT)}
                  aria-hidden={true}
                  onChange={(e) => handleFileChosen(PDF_SLOT, e)}
                />
              </>
            )}
          </div>
        </>
      )}

      <Modal
        show={preview.open}
        onHide={() => setPreview({ open: false, kind: null, src: null })}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {preview.kind === "image" && preview.src ? (
            <img
              src={preview.src}
              alt=""
              style={{ maxWidth: "100%", maxHeight: "70vh" }}
            />
          ) : null}
          {preview.kind === "video" && preview.src ? (
            <video
              src={preview.src}
              controls
              style={{ maxWidth: "100%", maxHeight: "70vh" }}
            />
          ) : null}
          {preview.kind === "pdf" && preview.src ? (
            <iframe
              title="PDF preview"
              src={preview.src}
              style={{ width: "100%", height: "70vh", border: "none" }}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-end border-top">
          <button
            type="button"
            className="btn btn-dark"
            onClick={() => setPreview({ open: false, kind: null, src: null })}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(deleteConfirm)}
        onHide={() => setDeleteConfirm(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete this item?</Modal.Title>
        </Modal.Header>
        <Modal.Body>This action cannot be undone.</Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteConfirm(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={Boolean(deletingSlot)}
            onClick={runDelete}
          >
            {deletingSlot ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SiteInstructionsSection;
